import { observable, keys, values, has, get, set, action, entries, toJS, remove } from 'mobx';


class FilterList {
  @observable availableFilters = {};
  @observable checkedFilters = {};
  @observable openedFilters = {};
  @observable anchorEl = null;

  constructor(availableFilters, onChange) {
    this.onChange = onChange;
    this.update(availableFilters);
  }

  get isOpen() {
    return Boolean(this.anchorEl);
  }

  isFilterOpen = filter => {
    return get(this.openedFilters, filter);
  };

  @action openFilter = filter => {
    if (!this.isFilterOpen(filter)) {
      this.clearOpenedFilters();
      set(this.openedFilters, filter, true);
    }
  };

  @action closeFilter = filter => {
    if (this.isFilterOpen(filter)) {
      set(this.openedFilters, filter, false);
    }
  };

  getAvailableValues = filter => {
    return get(this.availableFilters, filter);
  };

  get shouldFilterFilters() {
    return this.availableFilters.length > 0;
  }

  get filteredCheckedFilters() {
    let checkedFilters = entries(this.checkedFilters);
    if (this.shouldFilterFilters) {
      checkedFilters = checkedFilters.filter(([key, value]) => has(this.availableFilters, key));
    }
    return Object.fromEntries(
      checkedFilters.map(
        ([filter, checkedValues]) =>
          [filter, toJS(this.getFilteredCheckedValues(filter))]
      )
    );
  }

  getCheckedValues = filter => {
    let checkedValues = get(this.checkedFilters, filter);
    if (!checkedValues) {
      set(this.checkedFilters, filter, []);
      checkedValues = get(this.checkedFilters, filter);
    }
    return checkedValues;
  };

  getFilteredCheckedValues = filter => {
    const checkedValues = this.getCheckedValues(filter);
    if (this.shouldFilterFilters) {
      return checkedValues.filter(
        value => this.availableFilters[filter].indexOf(value) >= 0
      );
    }
    return checkedValues;
  };

  isChecked = (filter, value) => {
    const checkedValues = this.getCheckedValues(filter);
    return (checkedValues !== undefined) && (checkedValues.indexOf(value) !== -1);
  };

  @action setValues = (filter, values, triggerChange=true) => {
    const storedValues = this.getCheckedValues(filter);
    if (values !== storedValues) {
      storedValues.clear();
      storedValues.push(...values);
      if (triggerChange) {
        this.triggerChange();
      }
    }
  };

  @action checkValue = (filter, value, triggerChange=true) => {
    if (value === null) {
      return false;
    }
    const filterValue = this.getCheckedValues(filter);
    if (!this.isChecked(filter, value)) {
      filterValue.push(value);
      if (triggerChange) {
        this.triggerChange();
      }
    }
    return true;
  };

  count = () => {
    let count = 0;
    for (const key of Object.keys(this.filteredCheckedFilters)) {
      count += this.countFilter(key);
    }
    return count;
  };

  countFilter = filter => {
    return this.getFilteredCheckedValues(filter).length;
  };

  @action close = () => {
    this.anchorEl = null;
  };

  @action open = event => {
    this.anchorEl = event.currentTarget;
  };

  triggerChange = () => {
    if (this.onChange) {
      this.onChange();
    }
  };

  @action clearOpenedFilters = () => {
    for (const filter of keys(this.openedFilters)) {
      if (get(this.openedFilters, filter)) {
        set(this.openedFilters, filter, false);
      }
    }
  };

  @action clear = () => {
    for (const checkedValues of values(this.checkedFilters)) {
      checkedValues.clear();
    }
    this.clearOpenedFilters();
    this.triggerChange();
  };

  @action removeUnrecognizedFilters = () => {
    for (const key of Object.keys(this.checkedFilters)) {
      if (!has(this.availableFilters, key)) {
        remove(this.checkedFilters, key);
      }
    }
  };

  @action update = availableFilters => {
    for (const [key, value] of Object.entries(availableFilters)) {
      if (!has(this.checkedFilters, key)) {
        set(this.checkedFilters, key, []);
      }
      if (!has(this.openedFilters, key)) {
        set(this.openedFilters, key, false);
      }
      set(this.availableFilters, key, value);
    }
    this.removeUnrecognizedFilters();
  }
}


export default FilterList;
